/**
 * InvoiceDetailPage - Invoice detail view
 * PROMPT 40: Financial Documents Unified View Layer
 * PROMPT 41: Financial Communications - Email integration
 * EF-QUOTE-DETAIL-ALIGN-01: Aligned with PDF grouped structure
 * EF-CREDIT-NOTE-UI-V1: Credit Note integration
 * EF-INVOICE-SYSTEM-ALIGNMENT-V1: Uses view model for SSOT
 * EF-INVOICE-PAYMENT-TRACKING-V1: Payment history and add payment
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  getInvoice,
  getInvoiceViewModel,
  getProject,
  getClient,
  getPlatformSettings,
  getInvoicePdfUrl,
  sendInvoiceEmail,
  getInvoiceEmailLog,
  getInvoiceCreditSummary,
  getInvoiceCreditNotes,
  createCreditNoteFromInvoice,
  addInvoicePayment,
  deleteInvoicePayment,
  viewInvoicePdf,
  downloadInvoicePdf,
  extractErrorMessage,
} from "../lib/api";
import { useFormatters } from "../components/shared/useFormatters";
import { LoadingSpinner } from "../components/shared/DataComponents";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  DocumentHeader,
  DocumentDetails,
  GroupedLineItems,
  TotalsSummary,
  DocumentFooter,
  DocumentActions,
  EmailComposeModal,
  EmailLogSection,
  printStyles,
} from "../components/financial";
import { toast } from "sonner";
import { Plus, FileText, ExternalLink, CreditCard, Trash2, DollarSign } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
// EVENTSFLO-CLIENT-CREDIT-UI-FORM-V1
import AddServiceCreditDialog, { CreditHistoryTable } from "../components/financial/AddServiceCreditDialog";
// EVENTSFLO-PDF-SUBTOTAL-BEFORE-CREDITS-V1
import EditableClientNotes from "../components/financial/EditableClientNotes";

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatDate, formatCurrency, currencySymbol: platformCurrencySymbol } = useFormatters();
  
  const [invoice, setInvoice] = useState(null);
  const [viewModel, setViewModel] = useState(null);  // EF-INVOICE-SYSTEM-ALIGNMENT-V1
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  
  // EF-CREDIT-NOTE-UI-V1: Credit note state
  const [creditSummary, setCreditSummary] = useState(null);
  const [creditNotes, setCreditNotes] = useState([]);
  const [creatingCreditNote, setCreatingCreditNote] = useState(false);

  // EVENTSFLO-CLIENT-CREDIT-UI-FORM-V1
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  
  // EF-INVOICE-PAYMENT-TRACKING-V1: Payment state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [newPayment, setNewPayment] = useState({
    amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    method: "bank_transfer",
    reference: ""
  });
  
  // EF-INVOICE-SYSTEM-ALIGNMENT-V1: Use view model totals, with fallback
  const totals = viewModel?.totals || {};
  const payments = viewModel?.payments || [];
  const currencySymbol = totals.currency_symbol || invoice?.currency_symbol || platformCurrencySymbol;
  
  // EF-CREDIT-NOTE-UI-V1: Check if credit note creation is allowed
  // Allowed for: issued, sent, paid, partial, overdue (not draft or cancelled)
  const canCreateCreditNote = invoice && 
    ["issued", "sent", "paid", "partial", "overdue"].includes(invoice.status);
  
  // EF-INVOICE-PAYMENT-TRACKING-V1: Check if adding payment is allowed
  const canAddPayment = invoice && 
    ["issued", "sent", "partial", "overdue"].includes(invoice.status) &&
    totals.balance_due > 0;

  const loadInvoice = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      // EF-INVOICE-SYSTEM-ALIGNMENT-V1: Load view model as primary data source
      const vmRes = await getInvoiceViewModel(id);
      setViewModel(vmRes.data);
      
      // Also load raw invoice for status checks
      const invRes = await getInvoice(id);
      const invoiceData = invRes.data;
      setInvoice(invoiceData);

      // Load related data in parallel
      const promises = [];
      
      if (invoiceData.project_id) {
        promises.push(
          getProject(invoiceData.project_id)
            .then((res) => setProject(res.data))
            .catch(() => null)
        );
      }
      
      if (invoiceData.client_id) {
        promises.push(
          getClient(invoiceData.client_id)
            .then((res) => setClient(res.data))
            .catch(() => null)
        );
      }
      
      promises.push(
        getPlatformSettings()
          .then((res) => setCompanyProfile(res.data))
          .catch(() => null)
      );
      
      // EF-CREDIT-NOTE-UI-V1: Load credit summary and credit notes
      promises.push(
        getInvoiceCreditSummary(id)
          .then((res) => setCreditSummary(res.data))
          .catch(() => null)
      );
      
      promises.push(
        getInvoiceCreditNotes(id)
          .then((res) => setCreditNotes(res.data || []))
          .catch(() => null)
      );
      
      await Promise.all(promises);
    } catch (err) {
      console.error("Failed to load invoice:", extractErrorMessage(err));
      toast.error("Failed to load invoice");
      navigate("/invoices");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadEmailLogs = useCallback(async () => {
    if (!id) return;
    setEmailLogsLoading(true);
    try {
      const res = await getInvoiceEmailLog(id);
      setEmailLogs(res.data || []);
    } catch (err) {
      console.error("Failed to load email logs:", extractErrorMessage(err));
    } finally {
      setEmailLogsLoading(false);
    }
  }, [id]);

  // EF-CREDIT-NOTE-UI-V1: Create credit note handler
  const handleCreateCreditNote = async () => {
    setCreatingCreditNote(true);
    try {
      const res = await createCreditNoteFromInvoice(id);
      toast.success("Credit note created");
      navigate(`/credit-notes/${res.data.id}`);
    } catch (err) {
      console.error("Failed to create credit note:", extractErrorMessage(err));
      toast.error("Failed to create credit note: " + extractErrorMessage(err));
    } finally {
      setCreatingCreditNote(false);
    }
  };

  // EF-INVOICE-PAYMENT-TRACKING-V1: Add payment handler
  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    
    if (parseFloat(newPayment.amount) > totals.balance_due) {
      toast.error(`Payment cannot exceed balance due (${currencySymbol}${totals.balance_due.toFixed(2)})`);
      return;
    }
    
    setPaymentSubmitting(true);
    try {
      await addInvoicePayment(id, {
        amount: parseFloat(newPayment.amount),
        payment_date: newPayment.payment_date,
        method: newPayment.method,
        reference: newPayment.reference || null
      });
      
      toast.success(`Payment of ${currencySymbol}${parseFloat(newPayment.amount).toFixed(2)} recorded`);
      
      // Reset form and refresh data
      setNewPayment({
        amount: "",
        payment_date: new Date().toISOString().split("T")[0],
        method: "bank_transfer",
        reference: ""
      });
      setShowPaymentForm(false);
      loadInvoice(); // Refresh to get updated view model
      
    } catch (err) {
      console.error("Failed to add payment:", extractErrorMessage(err));
      toast.error("Failed to add payment: " + extractErrorMessage(err));
    } finally {
      setPaymentSubmitting(false);
    }
  };

  // EF-INVOICE-PAYMENT-TRACKING-V1: Delete payment handler
  const handleDeletePayment = async (paymentId) => {
    if (!confirm("Are you sure you want to delete this payment?")) return;
    
    try {
      await deleteInvoicePayment(id, paymentId);
      toast.success("Payment deleted");
      loadInvoice(); // Refresh
    } catch (err) {
      toast.error("Failed to delete payment: " + extractErrorMessage(err));
    }
  };

  useEffect(() => {
    loadInvoice();
    loadEmailLogs();
  }, [loadInvoice, loadEmailLogs]);

  const handleSendEmail = async (emailData) => {
    await sendInvoiceEmail(id, emailData);
    // Refresh email logs after sending
    loadEmailLogs();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <p>Invoice not found</p>
        </div>
      </div>
    );
  }

  // EF-INVOICE-SYSTEM-ALIGNMENT-V1: Use pre-calculated totals from view model
  // NO frontend calculations - view model is the single source of truth
  const subtotal = totals.subtotal || 0;
  const taxRate = totals.tax_rate || invoice.tax_rate || 20;
  const taxAmount = totals.vat_amount || 0;
  const total = totals.total || 0;
  const amountPaid = totals.amount_paid || invoice.amount_paid || 0;
  const balanceDue = totals.balance_due || (total - amountPaid);
  
  // EF-CREDIT-NOTE-UI-V1: Use credit summary for balance calculation
  const creditedTotalTTC = creditSummary?.credited_total_ttc || 0;
  const balanceAfterCredits = creditSummary?.balance_due_after_credits ?? balanceDue;
  const hasCredits = creditedTotalTTC !== 0;

  const pdfUrlSummary = getInvoicePdfUrl(id, 'summary');
  const pdfUrlDetailed = getInvoicePdfUrl(id, 'detailed');

  return (
    <div 
      className="min-h-screen bg-surface-base py-8 px-4 print:bg-white print:p-0"
      data-testid="invoice-detail-page"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Actions Bar */}
        <div className="flex items-center justify-between gap-4 print:hidden">
          <DocumentActions
            onBack={() => navigate("/invoices")}
            backLabel="Back to Invoices"
            pdfUrlSummary={pdfUrlSummary}
            pdfUrlDetailed={pdfUrlDetailed}
            downloadFileName={`invoice-${invoice.invoice_number || id}.pdf`}
            showSendEmail={true}
            onSendEmail={() => setEmailModalOpen(true)}
            onPreviewSummary={() => viewInvoicePdf(id, invoice.invoice_number, 'summary')}
            onPreviewDetailed={() => viewInvoicePdf(id, invoice.invoice_number, 'detailed')}
            onDownloadSummary={() => downloadInvoicePdf(id, invoice.invoice_number, 'summary')}
            onDownloadDetailed={() => downloadInvoicePdf(id, invoice.invoice_number, 'detailed')}
          />
          
          {/* EF-CREDIT-NOTE-UI-V1: Create Credit Note button */}
          {canCreateCreditNote && (
            <Button
              onClick={handleCreateCreditNote}
              disabled={creatingCreditNote}
              variant="outline"
              className="gap-2 border-red-200 text-red-700 hover:bg-red-50"
              data-testid="create-credit-note-btn"
            >
              <Plus className="h-4 w-4" />
              {creatingCreditNote ? "Creating..." : "Create Credit Note"}
            </Button>
          )}

          {/* EVENTSFLO-CLIENT-CREDIT-UI-FORM-V1 — Add service credit */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCreditDialogOpen(true)}
            data-testid="invoice-add-service-credit-btn"
          >
            Add service credit
          </Button>
        </div>
        <AddServiceCreditDialog
          open={creditDialogOpen}
          onOpenChange={setCreditDialogOpen}
          documentKind="invoice"
          documentId={id}
          documentStatus={invoice.status}
          currencySymbol={currencySymbol}
          onSuccess={() => { loadInvoice(); }}
        />
        <div className="print:hidden">
          <CreditHistoryTable
            lines={invoice.lines || []}
            currencySymbol={currencySymbol}
          />
        </div>
        <div className="print:hidden">
          <EditableClientNotes
            documentKind="invoice"
            documentId={id}
            initialNotes={invoice.notes || ""}
            onSaved={loadInvoice}
          />
        </div>

        {/* Invoice Card */}
        <Card className="overflow-hidden bg-white print:shadow-none print:rounded-none">
          {/* Header */}
          <DocumentHeader
            documentType="INVOICE"
            documentNumber={invoice.invoice_number}
            status={invoice.status}
            companyProfile={companyProfile}
            headerText={companyProfile?.invoice_header}
          />

          {/* Details */}
          <DocumentDetails
            documentType="invoice"
            issueDate={invoice.issue_date}
            dueDate={invoice.due_date}
            project={project}
            client={client}
            formatDate={formatDate}
          />

          {/* Line Items - Grouped by Shift (matches PDF structure) */}
          <GroupedLineItems
            lines={invoice.lines}
            formatCurrency={formatCurrency}
            testIdPrefix="invoice-line"
          />

          {/* Totals */}
          <div className="px-6 pb-6">
            <TotalsSummary
              subtotal={subtotal}
              taxRate={taxRate}
              taxAmount={taxAmount}
              total={total}
              amountPaid={amountPaid}
              balanceDue={balanceDue}
              currencySymbol={currencySymbol}
              taxLabel={totals.tax_label || companyProfile?.tax_label || "TVA"}
              primaryColor={companyProfile?.primary_color || "#0d9488"}
            />
            
            {/* EF-CREDIT-NOTE-UI-V1: Credit Summary Section */}
            {hasCredits && (
              <div className="mt-4 pt-4 border-t border-border" data-testid="credit-summary-section">
                <div className="flex justify-end">
                  <div className="w-full max-w-xs space-y-2">
                    <div className="flex justify-between py-2 text-sm">
                      <span className="text-muted-foreground">Credits Applied</span>
                      <span className="font-mono font-medium text-red-600" data-testid="credited-total-ttc">
                        {currencySymbol}{creditedTotalTTC.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 text-sm font-semibold">
                      <span className="text-foreground">Balance After Credits</span>
                      <span 
                        className="font-mono" 
                        style={{ color: balanceAfterCredits > 0 ? '#dc2626' : '#059669' }}
                        data-testid="balance-after-credits"
                      >
                        {currencySymbol}{balanceAfterCredits.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* EF-CREDIT-NOTE-UI-V1: Credit Notes List */}
          {creditNotes.length > 0 && (
            <div className="px-6 pb-6 print:hidden">
              <h3 className="font-semibold text-sm mb-3 text-muted-foreground">Related Credit Notes</h3>
              <div className="space-y-2">
                {creditNotes.map((cn) => (
                  <Link
                    key={cn.id}
                    to={`/credit-notes/${cn.id}`}
                    className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    data-testid={`credit-note-link-${cn.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-red-600" />
                      <div>
                        <span className="font-medium">
                          {cn.credit_note_number || "Draft Credit Note"}
                        </span>
                        <Badge 
                          variant="outline" 
                          className={`ml-2 text-xs ${
                            cn.status === 'issued' 
                              ? 'bg-green-50 text-green-700 border-green-200' 
                              : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                          }`}
                        >
                          {cn.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-red-600 font-medium">
                        {currencySymbol}{(cn.total_ttc_snapshot || 0).toFixed(2)}
                      </span>
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* EF-INVOICE-PAYMENT-TRACKING-V1: Payment History Section */}
          <div className="px-6 pb-6 print:hidden" data-testid="payment-history-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm text-muted-foreground flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Payment History
              </h3>
              {canAddPayment && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowPaymentForm(!showPaymentForm)}
                  data-testid="add-payment-btn"
                >
                  <DollarSign className="h-4 w-4 mr-1" />
                  Add Payment
                </Button>
              )}
            </div>
            
            {/* Add Payment Form */}
            {showPaymentForm && canAddPayment && (
              <form onSubmit={handleAddPayment} className="mb-4 p-4 border border-border rounded-lg bg-muted/30">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="payment-amount">Amount ({currencySymbol})</Label>
                    <Input
                      id="payment-amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={totals.balance_due}
                      value={newPayment.amount}
                      onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                      placeholder={`Max: ${totals.balance_due?.toFixed(2)}`}
                      required
                      data-testid="payment-amount-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="payment-date">Date</Label>
                    <Input
                      id="payment-date"
                      type="date"
                      value={newPayment.payment_date}
                      onChange={(e) => setNewPayment({ ...newPayment, payment_date: e.target.value })}
                      required
                      data-testid="payment-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="payment-method">Method</Label>
                    <Select
                      value={newPayment.method}
                      onValueChange={(value) => setNewPayment({ ...newPayment, method: value })}
                    >
                      <SelectTrigger id="payment-method" data-testid="payment-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="check">Check</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="payment-reference">Reference</Label>
                    <Input
                      id="payment-reference"
                      type="text"
                      value={newPayment.reference}
                      onChange={(e) => setNewPayment({ ...newPayment, reference: e.target.value })}
                      placeholder="e.g., Transfer #123"
                      data-testid="payment-reference-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowPaymentForm(false)}
                    disabled={paymentSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={paymentSubmitting}
                    data-testid="submit-payment-btn"
                  >
                    {paymentSubmitting ? "Recording..." : "Record Payment"}
                  </Button>
                </div>
              </form>
            )}
            
            {/* Payment List */}
            {payments.length > 0 ? (
              <div className="space-y-2">
                {payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-3 border border-border rounded-lg bg-green-50/30"
                    data-testid={`payment-row-${payment.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-green-100">
                        <CreditCard className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <div className="font-medium text-green-700">
                          {currencySymbol}{payment.amount.toFixed(2)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(payment.payment_date)} • {payment.method.replace("_", " ")}
                          {payment.reference && ` • Ref: ${payment.reference}`}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeletePayment(payment.id)}
                      data-testid={`delete-payment-${payment.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4 border border-dashed border-border rounded-lg">
                No payments recorded yet
              </p>
            )}
          </div>

          {/* Footer */}
          <DocumentFooter
            notes={invoice.notes}
            paymentTerms={companyProfile?.payment_terms}
            bankDetails={companyProfile?.bank_details}
            footerText={companyProfile?.invoice_footer}
            formatDate={formatDate}
          />
        </Card>

        {/* Email Log - Print hidden */}
        <div className="print:hidden">
          <EmailLogSection logs={emailLogs} loading={emailLogsLoading} />
        </div>
      </div>

      {/* Print Styles */}
      <style>{printStyles}</style>

      {/* Email Compose Modal */}
      <EmailComposeModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        documentType="invoice"
        documentId={id}
        documentNumber={invoice.invoice_number}
        documentData={{
          total,
          dueDate: formatDate(invoice.due_date),
          balanceDue,
        }}
        clientId={invoice.client_id}
        clientEmail={client?.contact_email}
        clientName={client?.name}
        companyName={companyProfile?.company_name}
        currencySymbol={currencySymbol}
        paymentTerms={companyProfile?.payment_terms}
        projectName={project?.name}
        onSend={handleSendEmail}
      />
    </div>
  );
}
